import {
  collection, doc, setDoc, updateDoc, deleteDoc, deleteField, getDocs, getDoc,
  onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, getBlob, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase.js';
import { deleteStorageFolder } from './courses.js';
import { createIdempotencyKey, runClientIdempotent } from './idempotency.js';

const searchMyTopicsFn = httpsCallable(functions, 'searchMyTopics');
const searchAllMyTopicsFn = httpsCallable(functions, 'searchAllMyTopics');

const topicsCol = (uid, courseId) => collection(db, 'users', uid, 'courses', courseId, 'topics');
const topicDoc = (uid, courseId, topicId) => doc(db, 'users', uid, 'courses', courseId, 'topics', topicId);

export const MAX_VERSIONS = 5;

// contentMode is purely descriptive metadata chosen in the "New topic"
// dialog (see openTopicModal in views/course.js) — it does not change what
// the generation backend is allowed to do (window.askAI is always available
// to every generated page per functions/systemPrompt.js). 'dynamic' just
// means the creator told us up front they intend to use the in-page AI
// bridge, so the UI can carry that intent into the Studio chat as a
// well-formed first instruction instead of leaving it to a freeform prompt.
// referenceCourseId/referenceTopicId (both optional, and referenceTopicId
// implies referenceCourseId) come from the "Reference existing content"
// picker in the New Topic dialog (views/course.js openTopicModal) — the
// creator pointing at ONE topic they've already built (any course of
// theirs, per the picker) whose page should guide this new topic's first
// generation. referenceMode says HOW: 'style' (default) means guide the
// QUALITY/STRUCTURE only, not the subject; 'source' means the reference
// covers the SAME subject and its content should be built from directly
// (e.g. referencing "Pay Rules" while creating "Visual diagram of Pay
// Rules"). The picker always asks which mode applies rather than guessing
// from the titles — see views/course.js. referenceTopicTitle is a
// denormalized snapshot purely for display (e.g. a "based on: …" note in
// the Studio) — it can go stale if the referenced topic is renamed later,
// which is fine, it's a label, not a live pointer; storagePath/
// currentVersion below are the only fields ever read to fetch content.
// Storing the pointer here (not just in sessionStorage like the dynamic-
// topic recipe) is deliberate: unlike that recipe, which is consumed and
// discarded on the first Studio message, the reference is worth keeping on
// the topic doc permanently as provenance, even though it's only ever
// USED for that first generation (see studio.js).
export async function createTopic(uid, courseId, {
  title, type = 'lesson', parentTopicId = null, contentMode = 'static',
  referenceCourseId = null, referenceTopicId = null, referenceTopicTitle = null, referenceMode = 'style',
  idempotencyKey = createIdempotencyKey('createTopic'),
}) {
  return runClientIdempotent({
    uid,
    operation: 'createTopic',
    key: idempotencyKey,
    payload: { courseId, title, type, parentTopicId, contentMode, referenceCourseId, referenceTopicId, referenceTopicTitle, referenceMode },
    handler: async () => {
      const ref = doc(topicsCol(uid, courseId));
      await setDoc(ref, {
        title, type, parentTopicId,
        // ownerUid: lets searchAllMyTopics (functions/search.js) scope a
        // collectionGroup('topics') query to just this student's own
        // topics — the doc's Firestore PATH already proves ownership for
        // every other read/write here, but a collection-GROUP query can't
        // see path segments the way a direct path query can, so this field
        // is the only thing that makes cross-course search possible at all.
        ownerUid: uid,
        contentMode,
        referenceCourseId: referenceTopicId ? referenceCourseId : null,
        referenceTopicId: referenceTopicId || null,
        referenceTopicTitle: referenceTopicId ? referenceTopicTitle : null,
        referenceMode: referenceTopicId ? (referenceMode === 'source' ? 'source' : 'style') : null,
        order: Date.now(),
        storagePath: null,
        currentVersion: 0,
        // See lib/courses.js createCourse — same resale-guard provenance
        // stamp, at the topic level (a topic-only marketplace listing is
        // checked against the TOPIC's own resalable flag, not the course's).
        resalable: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        idempotencyKey,
      });
      return ref.id;
    },
  });
}

// ── Index items ──────────────────────────────────────────────────────────
// A pure book-index-style container: a name that groups other items (index
// items or content topics) with no generated content of its own. Lives in
// the same topics subcollection as content topics, discriminated by
// kind: 'index' (every existing/future content topic simply has no `kind`
// field). parentTopicId works the same way it does for content topics, but
// index items may nest arbitrarily deep under other index items — see
// renderTree/renderTreeNode in views/course.js for the hierarchy rules
// (content topics stay capped at one level; an index item's parent must be
// null or another index item, never a content topic).
export async function createIndexItem(uid, courseId, {
  title, parentTopicId = null,
  idempotencyKey = createIdempotencyKey('createIndexItem'),
}) {
  return runClientIdempotent({
    uid,
    operation: 'createIndexItem',
    key: idempotencyKey,
    payload: { courseId, title, parentTopicId },
    handler: async () => {
      const ref = doc(topicsCol(uid, courseId));
      await setDoc(ref, {
        title, kind: 'index', parentTopicId,
        ownerUid: uid, // see createTopic above for why this field exists
        order: Date.now(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        idempotencyKey,
      });
      return ref.id;
    },
  });
}

export async function updateTopicMeta(uid, courseId, topicId, patch) {
  await updateDoc(topicDoc(uid, courseId, topicId), { ...patch, updatedAt: serverTimestamp() });
}

export function watchTopics(uid, courseId, callback) {
  const q = query(topicsCol(uid, courseId), orderBy('order', 'asc'));
  return onSnapshot(q, (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

// ── Generation job tracking (see functions/topicPersistence.js) ─────────
// A topic doc's `activeGenerationJobId` points at
// users/{uid}/generationJobs/{jobId} (owner-read, server-write-only — see
// firestore.rules) while a generation is in flight — set/cleared only by
// the Cloud Function, regardless of which tab (if any) is still watching.
// src/views/studio.js subscribes to this on topic open so a Studio tab that
// reopens a topic mid-generation (tab close, navigation, or logout, then
// coming back) sees "still generating" and resumes watching instead of
// starting a conflicting second one.
export function watchGenerationJob(uid, jobId, callback) {
  return onSnapshot(doc(db, 'users', uid, 'generationJobs', jobId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// One-time counterpart of watchGenerationJob — used right after a stream
// read fails (a dropped connection, a proxy timeout) to check whether the
// server-side turn actually finished (or is still running) before treating
// it as a failure. functions/topicPersistence.js's header explains why this
// can happen: the server finishes and saves a generation independent of
// whether the browser is still connected, so a client-side stream error is
// NOT proof the turn didn't complete. Checking this before offering
// "Regenerate" avoids starting a genuinely new, independent turn (with its
// own jobId/message) that answers the same instruction a second time right
// under the first — the actual cause of the repeated-chat-entries reports.
export async function getGenerationJob(uid, jobId) {
  const snap = await getDoc(doc(db, 'users', uid, 'generationJobs', jobId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// One-time fetch (no live subscription) — used by the "file a purchase
// under a topic" picker, which is opened inside a short-lived modal.
// Trashed topics are excluded for the same reason trashed courses are
// excluded from getCourses().
export async function getTopics(uid, courseId) {
  const snap = await getDocs(query(topicsCol(uid, courseId), orderBy('order', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => !t.deletedAt);
}

export async function getTopic(uid, courseId, topicId) {
  const snap = await getDoc(topicDoc(uid, courseId, topicId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ── Recycle Bin (soft delete) ───────────────────────────────────────────
// Mirrors trashCourse/restoreCourse in lib/courses.js — see that comment
// for the full picture. "Delete" on a topic stamps deletedAt rather than
// touching any data; deleteTopic() below stays the permanent path.
//
// Also cascades to any (currently active) direct subtopics: nesting is
// only ever one level deep here, and leaving a subtopic active while its
// parent vanishes from the tree would strand it — it'd have nowhere to
// render until the parent is restored. Each cascaded subtopic still gets
// its own deletedAt and its own Restore/Delete-forever entry in the Trash
// modal, so it isn't glued to the parent's fate.
//
// Index items (kind: 'index') are the one exception to cascading: since
// they can nest arbitrarily deep, silently cascading a whole subtree into
// the trash would be a much bigger blast radius than losing one subtopic.
// Instead, trashing an index item is blocked until it's empty — the user
// moves or removes its contents first. The permanent "Delete forever" path
// (functions/marketplace.js deleteTopicForeverImpl) enforces the same rule
// server-side, since that callable can be reached without going through
// trashTopic at all.
export async function trashTopic(uid, courseId, topicId) {
  const snap = await getDocs(query(topicsCol(uid, courseId), orderBy('order', 'asc')));
  const targetDoc = snap.docs.find((d) => d.id === topicId);
  const children = snap.docs.filter((d) => d.data().parentTopicId === topicId && !d.data().deletedAt);

  if (targetDoc?.data().kind === 'index') {
    if (children.length > 0) {
      throw new Error('This index item still has items inside it. Move or remove them first.');
    }
    await updateDoc(topicDoc(uid, courseId, topicId), { deletedAt: serverTimestamp() });
    return;
  }

  await Promise.all(children.map((d) => updateDoc(d.ref, { deletedAt: serverTimestamp() })));
  await updateDoc(topicDoc(uid, courseId, topicId), { deletedAt: serverTimestamp() });
}

export async function restoreTopic(uid, courseId, topicId) {
  await updateDoc(topicDoc(uid, courseId, topicId), { deletedAt: deleteField() });
}

// PERMANENT, unrecoverable delete — used for "Delete forever" out of the
// Recycle Bin, the 7-day auto-purge, and cascaded course deletion. The
// normal "Delete" button in the UI calls trashTopic() above instead.
export async function deleteTopic(uid, courseId, topicId) {
  const messagesSnap = await getDocs(collection(db, 'users', uid, 'courses', courseId, 'topics', topicId, 'messages'));
  await Promise.all(messagesSnap.docs.map((m) => deleteDoc(m.ref)));
  await deleteStorageFolder(`userFiles/${uid}/${courseId}/${topicId}`);
  await deleteDoc(topicDoc(uid, courseId, topicId));
}

// ── Versioned HTML file storage ─────────────────────────────────────────
// Every generation writes userFiles/{uid}/{courseId}/{topicId}/v{n}.html.
// Firestore only ever stores the storage path + current version number —
// the HTML body itself never touches a Firestore document (see README
// "Storage & size limits").

function versionPath(uid, courseId, topicId, version) {
  return `userFiles/${uid}/${courseId}/${topicId}/v${version}.html`;
}

export async function saveTopicVersion(uid, courseId, topicId, html, prevVersion, idempotencyKey = createIdempotencyKey('saveTopicVersion')) {
  return runClientIdempotent({
    uid,
    operation: 'saveTopicVersion',
    key: idempotencyKey,
    payload: { courseId, topicId, prevVersion, html },
    handler: async () => saveTopicVersionOnce(uid, courseId, topicId, html, prevVersion),
  });
}

// Marks a topic as needing a fresh search embedding — a plain, cheap
// Firestore field write (updateTopicMeta), no Cloud Function/secret
// involved, since stamping a timestamp needs neither. The actual embedding
// is computed later by functions/embeddingSweep.js (scheduled), once this
// topic has gone unstamped-over for a while — see that file's header for
// why generation/save paths never compute it inline. Call this fire-and-
// forget right after saveTopicVersion resolves, from any call site that
// saved a version WITHOUT going through server-side generation (which
// already stamps this itself — see functions/topicPersistence.js
// saveGeneratedVersion).
export function markTopicEmbeddingDirty(uid, courseId, topicId) {
  return updateTopicMeta(uid, courseId, topicId, { contentEmbeddingDirtyAt: serverTimestamp() }).catch(() => {});
}

// Semantic search over one course's own topics (see functions/search.js —
// v1 is deliberately per-course, not cross-course; see that file's header
// for why). Returns { results: [{ id, title, similarity }] }, ranked
// highest-similarity first, already filtered to a reasonable cutoff
// server-side (functions/semanticMatch.js's MIN_SIMILARITY) so an
// unrelated query returns an empty list rather than "closest of nothing
// good".
export async function searchMyTopics(courseId, query) {
  const res = await searchMyTopicsFn({ courseId, query });
  return res.data.results;
}

// Plain (non-semantic) title search across EVERY course the caller owns —
// the cross-course counterpart searchMyTopics above deliberately isn't.
// Returns { results: [{ id, courseId, title }] }; the caller already has
// each course's title/emoji from watchCourses, so there's no reason to
// duplicate that here.
export async function searchAllMyTopics(query) {
  const res = await searchAllMyTopicsFn({ query });
  return res.data.results;
}

async function saveTopicVersionOnce(uid, courseId, topicId, html, prevVersion) {
  const nextVersion = (prevVersion || 0) + 1;
  const path = versionPath(uid, courseId, topicId, nextVersion);
  await uploadString(ref(storage, path), html, 'raw', { contentType: 'text/html' });

  // Prune anything older than the last MAX_VERSIONS.
  const oldestToKeep = nextVersion - MAX_VERSIONS + 1;
  if (oldestToKeep > 1) {
    for (let v = 1; v < oldestToKeep; v++) {
      deleteObject(ref(storage, versionPath(uid, courseId, topicId, v))).catch(() => {});
      // The version's capability record is pruned with it — it describes a
      // file that no longer exists and is useless on its own.
      deleteObject(ref(storage, capabilityPath(uid, courseId, topicId, v))).catch(() => {});
    }
  }

  await updateTopicMeta(uid, courseId, topicId, {
    storagePath: `userFiles/${uid}/${courseId}/${topicId}/`,
    currentVersion: nextVersion,
  });
  return nextVersion;
}

export async function fetchVersionHtml(uid, courseId, topicId, version) {
  const blob = await getBlob(ref(storage, versionPath(uid, courseId, topicId, version)));
  return await blob.text();
}

export function listAvailableVersions(currentVersion) {
  const oldest = Math.max(1, currentVersion - MAX_VERSIONS + 1);
  const versions = [];
  for (let v = currentVersion; v >= oldest; v--) versions.push(v);
  return versions;
}

export async function downloadVersionUrl(uid, courseId, topicId, version) {
  return await getDownloadURL(ref(storage, versionPath(uid, courseId, topicId, version)));
}

// ── Per-version capability record ────────────────────────────────────────
// What version n's page COULD DO — a small JSON record (see
// src/lib/pageInventory.js for the text-level half and src/lib/sandbox.js's
// probePage for the behavioural half) written next to v{n}.html.
//
// Why Storage and not a Firestore field on the topic doc: the same rule the
// generated HTML follows (README "Storage & size limits"). This record is
// small today, but it grows with the page — one entry per control, one per
// canvas — and a per-version array field on a document is exactly the
// pattern that quietly walks into the 1 MiB cap after enough refinement
// turns. A sibling file per version also gets pruned by the same loop that
// prunes the HTML, with no extra bookkeeping.
//
// Everything here is FAIL-OPEN. A missing, unreadable or malformed record
// means "no baseline to compare against", which costs one turn of
// regression detection and nothing else. It must never break a generation.
function capabilityPath(uid, courseId, topicId, version) {
  return `userFiles/${uid}/${courseId}/${topicId}/v${version}.capabilities.json`;
}

export async function saveVersionCapabilities(uid, courseId, topicId, version, record) {
  if (!record || !version) return;
  try {
    await uploadString(
      ref(storage, capabilityPath(uid, courseId, topicId, version)),
      JSON.stringify(record),
      'raw',
      { contentType: 'application/json' },
    );
  } catch (_) { /* diagnostics only — never block the save the user is waiting on */ }
}

export async function fetchVersionCapabilities(uid, courseId, topicId, version) {
  if (!version) return null;
  try {
    const blob = await getBlob(ref(storage, capabilityPath(uid, courseId, topicId, version)));
    const parsed = JSON.parse(await blob.text());
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null; // no record for this version (e.g. a page generated before this feature shipped)
  }
}

// ── Per-version beat plan ────────────────────────────────────────────────
// The component/dependency plan a page was BUILT from, when it was built by
// the agentic beat pipeline (functions/beatPlanning.js). Stored the same way
// and for the same reasons as the capability record above — a small sibling
// JSON per version, in Storage rather than as a field on the topic doc.
//
// Why keep it at all: a beat-built page contains a real, invisible contract
// — `<!--LL_BEAT:id-->` markers, a shared `window.LL_STATE`, a shared
// `window.LL_tick` — and every LATER turn is an ordinary whole-file rewrite
// by a model that has never heard of any of it. Without this record, the
// contract quietly dies on the next "make the background blue", taking the
// page's correlated behaviour with it and leaving nothing to detect the
// loss. Sent back as `priorBeatPlan` on the next turn (see
// src/lib/generate.js and functions/systemPrompt.js's
// contractPreservationInstructions).
//
// FAIL-OPEN throughout, exactly like the capability record: a missing or
// unreadable plan costs one turn of contract protection — the server can
// still recover most of the contract from the page's own markup — and must
// never break a generation or a save.
function beatPlanPath(uid, courseId, topicId, version) {
  return `userFiles/${uid}/${courseId}/${topicId}/v${version}.beatplan.json`;
}

// SECOND PURPOSE — the investigative record. Beyond carrying the contract
// forward, this file is the only durable answer to "what was this page
// actually told to build". A beat-built simulation that comes out wrong is
// nearly impossible to diagnose from its own source: you cannot tell from
// the finished code whether a missing part was never specified, was
// specified and skipped, or was specified wrongly. The stored plan
// distinguishes those three, and `meta` records who wrote it (the admin's
// Simulation planner model), at what completeness mode, and whether the
// plan critic reviewed and revised it. Written at the same moment the html
// version is written, and keyed by the same version number, so the two
// always describe the same page.
export async function saveVersionBeatPlan(uid, courseId, topicId, version, beatPlan, meta) {
  if (!version || !Array.isArray(beatPlan) || beatPlan.length === 0) return;
  try {
    await uploadString(
      ref(storage, beatPlanPath(uid, courseId, topicId, version)),
      JSON.stringify({ beats: beatPlan, meta: meta || null, savedAt: Date.now() }),
      'raw',
      { contentType: 'application/json' },
    );
  } catch (_) { /* contract protection is best-effort — never block the save the user is waiting on */ }
}

export async function fetchVersionBeatPlan(uid, courseId, topicId, version) {
  const record = await fetchVersionBeatPlanRecord(uid, courseId, topicId, version);
  return record ? record.beats : null;
}

/**
 * The FULL stored record — `{ beats, meta, savedAt }` — for inspecting what
 * a version was specified to be. `fetchVersionBeatPlan` above returns only
 * the beats, because that is all the contract-preservation path wants; this
 * is the one to use when investigating a wrong simulation.
 *
 * Tolerant of older files written before `meta` existed (they parse fine,
 * `meta` simply comes back null), and fails open to null like every other
 * sidecar read here.
 */
export async function fetchVersionBeatPlanRecord(uid, courseId, topicId, version) {
  if (!version) return null;
  try {
    const blob = await getBlob(ref(storage, beatPlanPath(uid, courseId, topicId, version)));
    const parsed = JSON.parse(await blob.text());
    if (!parsed || !Array.isArray(parsed.beats) || !parsed.beats.length) return null;
    return { beats: parsed.beats, meta: parsed.meta || null, savedAt: parsed.savedAt || null };
  } catch (_) {
    return null; // no plan for this version — i.e. nearly every page, which is fine
  }
}

// ── Failed-attempt cache ─────────────────────────────────────────────────
// When a generation fails (server-side validation, or the client's own
// checks), whatever the model actually produced — even if broken — is
// cached here in Storage rather than just discarded. This means:
//   - it survives a page reload/closed tab, not just an in-memory variable
//   - "Regenerate" (see the studio view) can send this cached attempt back
//     to the model as something to fix/finish, which is cheaper and more
//     likely to converge than a blind from-scratch retry
//   - if a generation never fails again, this file is deleted on the next
//     success (clearFailedAttempt) so it doesn't linger as stale state
function failedAttemptPath(uid, courseId, topicId) {
  return `userFiles/${uid}/${courseId}/${topicId}/lastFailedAttempt.html`;
}

export async function saveFailedAttempt(uid, courseId, topicId, html) {
  if (!html || !html.trim()) return;
  await uploadString(ref(storage, failedAttemptPath(uid, courseId, topicId)), html, 'raw', {
    contentType: 'text/html',
  });
}

export async function fetchFailedAttempt(uid, courseId, topicId) {
  try {
    const blob = await getBlob(ref(storage, failedAttemptPath(uid, courseId, topicId)));
    return await blob.text();
  } catch (e) {
    return null; // no cached failed attempt, or not readable — treat as none
  }
}

export async function clearFailedAttempt(uid, courseId, topicId) {
  await deleteObject(ref(storage, failedAttemptPath(uid, courseId, topicId))).catch(() => {});
}
